import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, CardActions, CardContent, CardHeader, CardTitle, Chip, Typography } from '@client/toolkit';

import {
  AccountingPrinciple,
  PermissionKeys,
  useGetSpacesByIdPortfolioFinancialsQuery,
  usePutSpacesByIdPortfolioFinancialsMutation,
} from '@client/api';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';

export interface AccountingPrincipleCardProps {
  buildingId?: string;
}

const ACCOUNTING_PRINCIPLE_VALUES: AccountingPrinciple[] = ['Ngaap', 'Ifrs'];

// The regime is a required setting with NGAAP as the default for Norwegian private companies,
// so a missing PortfolioFinancials row (or unset value) is presented as NGAAP.
const DEFAULT_PRINCIPLE: AccountingPrinciple = 'Ngaap';

export function AccountingPrincipleCard({ buildingId }: AccountingPrincipleCardProps) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('portfolio');

  const { data: financials, isLoading } = useGetSpacesByIdPortfolioFinancialsQuery(
    { id: buildingId ?? '' },
    { skip: !buildingId },
  );

  const [upsert, { isLoading: isSaving }] = usePutSpacesByIdPortfolioFinancialsMutation();
  const { hasAnyPermission } = useSpacePermissions({ id: buildingId });
  const canEdit = hasAnyPermission([PermissionKeys.Space.Update]);

  const persistedPrinciple = financials?.accountingPrinciple ?? DEFAULT_PRINCIPLE;

  const [editing, setEditing] = useState(false);
  const [principle, setPrinciple] = useState<AccountingPrinciple>(DEFAULT_PRINCIPLE);

  useEffect(() => {
    setPrinciple(persistedPrinciple);
  }, [persistedPrinciple]);

  const handleEdit = () => setEditing(true);

  const handleCancel = () => {
    setPrinciple(persistedPrinciple);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!buildingId) return;
    try {
      // The upsert has patch semantics on the backend, so sending only the principle
      // leaves the other portfolio-financials fields untouched.
      await upsert({ id: buildingId, body: { accountingPrinciple: principle } }).unwrap();
      setEditing(false);
    } catch {
      // error handled by RTK
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex-1" variant="highlighted">
          {t('accountingPrinciple.title')}
        </CardTitle>
        <CardActions>
          {!editing ? (
            canEdit && (
              <Button size="sm" onClick={handleEdit} disabled={isLoading}>
                {tCommon('edit')}
              </Button>
            )
          ) : (
            <>
              <Button size="sm" color="primary-dark" onClick={handleCancel} disabled={isSaving}>
                {tCommon('cancel')}
              </Button>
              <Button size="sm" color="primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? t('saving') : tCommon('save')}
              </Button>
            </>
          )}
        </CardActions>
      </CardHeader>

      <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
        <div className="flex flex-col gap-3">
          <Typography variant="none" className="text-sm text-gray-400">
            {t('accountingPrinciple.description')}
          </Typography>

          {editing ? (
            <div className="flex gap-2">
              {ACCOUNTING_PRINCIPLE_VALUES.map((value) => (
                <Button
                  key={value}
                  size="sm"
                  color={principle === value ? 'primary' : 'primary-dark'}
                  onClick={() => setPrinciple(value)}
                  disabled={isSaving}
                >
                  {t(`accountingPrinciple.options.${value}`)}
                </Button>
              ))}
            </div>
          ) : (
            <div>
              <Chip title={t('accountingPrinciple.title')}>
                {isLoading ? '\u2013' : t(`accountingPrinciple.options.${persistedPrinciple}`)}
              </Chip>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
